import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Weekly } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import {
  Printer,
  ArrowLeft,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  CheckCircle2,
  Target,
  TrendingUp,
  Lightbulb,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { BillyMark } from "@/components/BillyLogo";
import {
  BarChart,
  Bar as RBar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

// Helpers
const startOfWeek = (d) => {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Monday = 0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (iso, n) => {
  const dt = new Date(iso);
  dt.setDate(dt.getDate() + n);
  return toIso(dt);
};

// Tip generator — purely rules-based, no LLM cost
const buildTips = (s, next) => {
  const tips = [];
  if (!s) return tips;

  // 1. On track / off track headline
  if (s.on_track) {
    tips.push({
      tone: "good",
      title: "You're on track to hit the profit goal this week.",
      body:
        "Keep doing what you're doing. As receivables land, log them on the Income tab so the Weekly view stays accurate.",
    });
  } else if (s.projected_shortfall > 0) {
    tips.push({
      tone: "warn",
      title: `Short by ${fmtMoney(s.projected_shortfall)} even if every booked job lands.`,
      body: `You're carrying ${fmtMoney(s.outgoing_total)} in bills and chasing ${fmtMoney(s.profit_goal)} profit, but only ${fmtMoney(s.incoming_total)} is booked. Log any pending install deposits, service callouts or repair jobs you're expecting this week to close the gap.`,
    });
  } else {
    tips.push({
      tone: "warn",
      title: "You'll hit the goal only if every booked job pays this week.",
      body: `${fmtMoney(s.still_needed)} still has to come through the door. Chase any client whose deposit or final invoice is overdue today, and confirm install dates with anyone scheduled for later this week.`,
    });
  }

  // 2. Overdue
  if (s.overdue_total > 0) {
    tips.push({
      tone: "bad",
      title: `${fmtMoney(s.overdue_total)} overdue across ${s.overdue_count} bill${s.overdue_count === 1 ? "" : "s"}.`,
      body:
        "Call or text the overdue customers today and offer the public pay-link from the Bill detail page. Even one paid invoice can flip this week from red to green.",
    });
  } else {
    tips.push({
      tone: "good",
      title: "No overdue bills — every account's current.",
      body:
        "Strong sign. Use the breathing room to chase next week's deposits early.",
    });
  }

  // 3. Bills-vs-received ratio coaching
  if (s.outgoing_total > 0 && s.incoming_received_total < s.outgoing_total * 0.5) {
    tips.push({
      tone: "warn",
      title: "Bills outpacing receipts so far this week.",
      body: `Only ${fmtMoney(s.incoming_received_total)} has been received against ${fmtMoney(s.outgoing_total)} owed. Push to bank at least 50% of outgoings by mid-week to avoid month-end stress.`,
    });
  }

  // 4. Profit goal too low / too aggressive (rough heuristic)
  if (s.profit_goal > 0 && s.outgoing_total > 0) {
    const ratio = s.profit_goal / s.outgoing_total;
    if (ratio > 2) {
      tips.push({
        tone: "info",
        title: "Profit goal looks aggressive vs current bill load.",
        body: `Your weekly profit goal of ${fmtMoney(s.profit_goal)} is more than double this week's outgoings. Make sure you've actually got the booked work to back it — or trim the goal so it's realistic.`,
      });
    } else if (ratio < 0.2 && s.outgoing_total > 200) {
      tips.push({
        tone: "info",
        title: "Profit goal may be too low for the work you're carrying.",
        body: `Your goal of ${fmtMoney(s.profit_goal)} is small relative to ${fmtMoney(s.outgoing_total)} in bills. Consider lifting it — you're doing the work, lock in the margin.`,
      });
    }
  }

  // 5. Next week heads-up
  if (next) {
    if (next.outgoing_total > 0 || next.incoming_total > 0) {
      const lead =
        next.outgoing_total > next.incoming_total
          ? "Heads up — bills outweigh booked income next week."
          : next.incoming_total > next.outgoing_total
            ? "Next week is shaping up positive."
            : "Next week is roughly break-even.";
      tips.push({
        tone: next.outgoing_total > next.incoming_total ? "warn" : "good",
        title: lead,
        body: `${fmtMoney(next.outgoing_total)} due out vs ${fmtMoney(next.incoming_total)} booked in (${fmtMoney(next.incoming_received_total)} already received). Log any expected deposits now so the forecast is accurate by Monday.`,
      });
    } else {
      tips.push({
        tone: "info",
        title: "Nothing logged for next week yet.",
        body:
          "Open the Income tab and pre-book any installs, repairs or service calls you've quoted. The Weekly view will then forecast the cashflow automatically.",
      });
    }
  }

  return tips;
};

const KPI = ({ label, value, accent = "#000", icon: Icon, hint }) => (
  <div className="border-2 border-black p-4">
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
        {label}
      </div>
      {Icon && <Icon className="h-4 w-4 text-neutral-400" />}
    </div>
    <div
      className="mt-2 font-display font-black text-2xl tabular-nums leading-tight"
      style={{ color: accent }}
    >
      {value}
    </div>
    {hint && (
      <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
        {hint}
      </div>
    )}
  </div>
);

const TipCard = ({ tip }) => {
  const tone =
    tip.tone === "good"
      ? "border-green-600 bg-green-50"
      : tip.tone === "bad"
        ? "border-red-500 bg-red-50"
        : tip.tone === "warn"
          ? "border-amber-500 bg-amber-50"
          : "border-neutral-300 bg-neutral-50";
  return (
    <div className={`border-2 ${tone} p-4 break-inside-avoid`}>
      <div className="flex items-start gap-3">
        <Lightbulb className="h-5 w-5 mt-0.5 text-black flex-shrink-0" />
        <div>
          <div className="font-display font-extrabold text-base leading-snug">
            {tip.title}
          </div>
          <div className="text-sm text-neutral-700 mt-1 leading-snug">
            {tip.body}
          </div>
        </div>
      </div>
    </div>
  );
};

const WeeklyPrint = () => {
  const navigate = useNavigate();
  const [thisWeek, setThisWeek] = useState(null);
  const [nextWeek, setNextWeek] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ws = toIso(startOfWeek(new Date()));
    const nws = addDays(ws, 7);
    Promise.all([Weekly.summary(ws), Weekly.summary(nws), Weekly.trend(6)])
      .then(([cur, nxt, t]) => {
        setThisWeek(cur);
        setNextWeek(nxt);
        setTrend(t);
      })
      .catch(() => toast.error("Could not load weekly print data."))
      .finally(() => setLoading(false));
  }, []);

  const tips = useMemo(() => buildTips(thisWeek, nextWeek), [thisWeek, nextWeek]);

  if (loading) {
    return (
      <div className="text-sm text-neutral-500 p-8">Loading weekly print…</div>
    );
  }
  if (!thisWeek) return null;

  const s = thisWeek;
  const printDate = new Date().toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const weekRange = `${fmtDate(s.week_start)} — ${fmtDate(s.week_end)}`;
  const chartData = [
    {
      label: "This week",
      Out: Math.round(s.outgoing_total || 0),
      Received: Math.round(s.incoming_received_total || 0),
      Expected: Math.round(s.incoming_total || 0),
    },
    nextWeek && {
      label: "Next week",
      Out: Math.round(nextWeek.outgoing_total || 0),
      Received: Math.round(nextWeek.incoming_received_total || 0),
      Expected: Math.round(nextWeek.incoming_total || 0),
    },
  ].filter(Boolean);

  return (
    <div
      data-testid="weekly-print"
      className="bg-white text-black min-h-screen"
    >
      {/* Action bar — hidden on print */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/weekly")}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Weekly
          </button>
          <button
            data-testid="print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Print page */}
      <div className="max-w-5xl mx-auto px-6 py-8 print:py-4 print:px-6">
        {/* Header */}
        <header className="border-b-2 border-black pb-5 mb-6 flex items-end justify-between gap-6">
          <div className="flex items-center gap-3">
            <BillyMark className="h-10 w-10" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                Weekly Cashflow Statement
              </div>
              <h1 className="font-display font-black text-3xl tracking-tight leading-tight">
                Billy. Weekly Report
              </h1>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
              Week of
            </div>
            <div className="font-display font-extrabold text-base">
              {weekRange}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Printed {printDate}
            </div>
          </div>
        </header>

        {/* KPI tiles */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <KPI
            label="Going Out"
            value={fmtMoney(s.outgoing_total)}
            accent="#FF3B30"
            icon={ArrowUpCircle}
            hint={`${s.bills.length} bill${s.bills.length === 1 ? "" : "s"}`}
          />
          <KPI
            label="Overdue"
            value={fmtMoney(s.overdue_total)}
            accent={s.overdue_total > 0 ? "#FF3B30" : "#008A00"}
            icon={AlertTriangle}
            hint={
              s.overdue_total > 0
                ? `${s.overdue_count} to clear`
                : "All current"
            }
          />
          <KPI
            label="Expected In"
            value={fmtMoney(s.incoming_expected_total)}
            accent="#B45309"
            icon={ArrowDownCircle}
            hint="Pending"
          />
          <KPI
            label="Received"
            value={fmtMoney(s.incoming_received_total)}
            accent="#008A00"
            icon={CheckCircle2}
            hint="In the door"
          />
          <KPI
            label="Still Needed"
            value={fmtMoney(s.still_needed)}
            accent={s.still_needed > 0 ? "#000" : "#008A00"}
            icon={Target}
            hint={s.still_needed > 0 ? "to hit goal" : "goal covered"}
          />
        </section>

        {/* Chart */}
        <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                Ins vs Outs
              </div>
              <div className="font-display font-extrabold text-lg leading-tight">
                This week & next, side by side
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-600">
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5"
                  style={{ background: "#FF3B30" }}
                />{" "}
                Out
              </span>
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5"
                  style={{ background: "#008A00" }}
                />{" "}
                Received
              </span>
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5"
                  style={{ background: "#002FA7" }}
                />{" "}
                Expected
              </span>
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-[2px] w-3"
                  style={{ background: "#111" }}
                />{" "}
                Target
              </span>
            </div>
          </div>
          <div
            data-testid="weekly-print-chart"
            style={{ width: "100%", height: 280, minHeight: 280 }}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 8, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="#E5E7EB"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: 12,
                    fontFamily: "IBM Plex Sans",
                    letterSpacing: "0.08em",
                    fontWeight: 700,
                  }}
                  stroke="#111"
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  stroke="#9CA3AF"
                  tickFormatter={(v) =>
                    v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                  }
                />
                <Tooltip
                  formatter={(v) =>
                    Number(v).toLocaleString("en-AU", {
                      style: "currency",
                      currency: "AUD",
                    })
                  }
                  contentStyle={{
                    borderRadius: 0,
                    border: "2px solid #111",
                    fontSize: 12,
                  }}
                />
                <ReferenceLine
                  y={s.target_in}
                  stroke="#111"
                  strokeDasharray="4 4"
                  label={{
                    value: `Target ${fmtMoney(s.target_in)}`,
                    position: "right",
                    fontSize: 10,
                    fill: "#111",
                  }}
                />
                <RBar dataKey="Out" fill="#FF3B30" />
                <RBar dataKey="Received" fill="#008A00" />
                <RBar dataKey="Expected" fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 text-sm text-neutral-700 leading-snug">
            Target this week is{" "}
            <span className="font-bold mono">{fmtMoney(s.target_in)}</span> (
            {fmtMoney(s.outgoing_total)} bills +{" "}
            {fmtMoney(s.profit_goal)} profit). You've banked{" "}
            <span className="font-bold mono">
              {fmtMoney(s.incoming_received_total)}
            </span>{" "}
            so far — still need{" "}
            <span className="font-bold mono">{fmtMoney(s.still_needed)}</span>{" "}
            to land before Sunday.
          </div>
        </section>

        {/* Tips */}
        <section className="mb-6 break-inside-avoid">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
            What to focus on this week
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, i) => (
              <TipCard key={i} tip={tip} />
            ))}
          </div>
        </section>

        {/* Outs detail */}
        <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUpCircle className="h-4 w-4 text-red-600" />
            <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
              Outs — Bills due this week
            </h2>
            <span className="ml-auto font-display font-extrabold mono tabular-nums">
              {fmtMoney(s.outgoing_total)}
            </span>
          </div>
          {s.bills.length === 0 ? (
            <div className="text-sm text-neutral-500">No bills due.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                  <th className="text-left py-2 font-bold">Company</th>
                  <th className="text-left py-2 font-bold">Invoice #</th>
                  <th className="text-left py-2 font-bold">Due</th>
                  <th className="text-right py-2 font-bold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {s.bills.map((b) => (
                  <tr key={b.id} className="border-b border-neutral-100">
                    <td className="py-2 font-display font-bold">
                      {b.company}
                    </td>
                    <td className="py-2 text-neutral-600 mono">
                      #{b.invoice_number}
                    </td>
                    <td className="py-2 text-neutral-600">
                      {fmtDate(b.due_date)}
                    </td>
                    <td className="py-2 text-right font-display font-extrabold mono tabular-nums">
                      {fmtMoney(b.remaining_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Overdue detail (if any) */}
        {s.overdue_bills && s.overdue_bills.length > 0 && (
          <section className="border-2 border-red-500 p-5 mb-6 break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
                Overdue bills
              </h2>
              <span className="ml-auto font-display font-extrabold mono tabular-nums text-red-700">
                {fmtMoney(s.overdue_total)}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                  <th className="text-left py-2 font-bold">Company</th>
                  <th className="text-left py-2 font-bold">Invoice #</th>
                  <th className="text-left py-2 font-bold">Was due</th>
                  <th className="text-right py-2 font-bold">Owed</th>
                </tr>
              </thead>
              <tbody>
                {s.overdue_bills.map((b) => (
                  <tr key={b.id} className="border-b border-neutral-100">
                    <td className="py-2 font-display font-bold">
                      {b.company}
                    </td>
                    <td className="py-2 text-neutral-600 mono">
                      #{b.invoice_number}
                    </td>
                    <td className="py-2 text-neutral-600">
                      {fmtDate(b.due_date)}
                    </td>
                    <td className="py-2 text-right font-display font-extrabold mono tabular-nums text-red-700">
                      {fmtMoney(b.remaining_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Ins detail */}
        <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
          <div className="flex items-center gap-2 mb-3">
            <ArrowDownCircle className="h-4 w-4 text-green-700" />
            <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
              Ins — Income this week
            </h2>
            <span className="ml-auto font-display font-extrabold mono tabular-nums">
              {fmtMoney(s.incoming_total)}
            </span>
          </div>
          {s.incomes.length === 0 ? (
            <div className="text-sm text-neutral-500">
              No income scheduled this week.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-300">
                  <th className="text-left py-2 font-bold">Client</th>
                  <th className="text-left py-2 font-bold">Category</th>
                  <th className="text-left py-2 font-bold">Expected</th>
                  <th className="text-left py-2 font-bold">Status</th>
                  <th className="text-right py-2 font-bold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {s.incomes.map((i) => (
                  <tr key={i.id} className="border-b border-neutral-100">
                    <td className="py-2 font-display font-bold">
                      {i.client_name}
                    </td>
                    <td className="py-2 text-neutral-600">{i.category}</td>
                    <td className="py-2 text-neutral-600">
                      {fmtDate(i.expected_date)}
                    </td>
                    <td className="py-2 text-[10px] uppercase tracking-widest font-bold">
                      {i.received ? (
                        <span className="text-green-700">Received</span>
                      ) : (
                        <span className="text-amber-700">Pending</span>
                      )}
                    </td>
                    <td
                      className={`py-2 text-right font-display font-extrabold mono tabular-nums ${i.received ? "text-green-700" : "text-amber-700"}`}
                    >
                      {fmtMoney(i.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Next week forecast */}
        {nextWeek && (
          <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays className="h-4 w-4" />
              <h2 className="font-display font-extrabold uppercase tracking-widest text-base">
                Next week's forecast
              </h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
                {fmtDate(nextWeek.week_start)} — {fmtDate(nextWeek.week_end)}
              </span>
            </div>

            <p className="text-sm text-neutral-700 leading-snug mb-4">
              {nextWeek.outgoing_total > nextWeek.incoming_total ? (
                <>
                  Next week tightens —{" "}
                  <span className="font-bold mono">
                    {fmtMoney(nextWeek.outgoing_total)}
                  </span>{" "}
                  in bills against just{" "}
                  <span className="font-bold mono">
                    {fmtMoney(nextWeek.incoming_total)}
                  </span>{" "}
                  in booked income. Book more work or chase deposits to swing
                  it positive.
                </>
              ) : nextWeek.incoming_total > 0 || nextWeek.outgoing_total > 0 ? (
                <>
                  Next week looks healthier —{" "}
                  <span className="font-bold mono">
                    {fmtMoney(nextWeek.incoming_total)}
                  </span>{" "}
                  booked in vs{" "}
                  <span className="font-bold mono">
                    {fmtMoney(nextWeek.outgoing_total)}
                  </span>{" "}
                  in bills. Keep logging quotes and deposits as they come
                  through.
                </>
              ) : (
                <>
                  Nothing booked for next week yet. Pre-load any installs,
                  repairs or quotes through the Income tab so the forecast is
                  accurate by Monday.
                </>
              )}{" "}
              Profit goal for next week:{" "}
              <span className="font-bold mono">
                {fmtMoney(nextWeek.profit_goal)}
              </span>
              . Still needed to hit it:{" "}
              <span className="font-bold mono">
                {fmtMoney(nextWeek.still_needed)}
              </span>
              .
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
                  Bills due next week
                </div>
                {nextWeek.bills.length === 0 ? (
                  <div className="text-sm text-neutral-500">None.</div>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {nextWeek.bills.map((b) => (
                      <li
                        key={b.id}
                        className="py-2 flex items-center justify-between gap-3 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-display font-bold truncate">
                            {b.company}
                          </div>
                          <div className="text-xs text-neutral-500 truncate">
                            #{b.invoice_number} · {fmtDate(b.due_date)}
                          </div>
                        </div>
                        <div className="font-display font-extrabold mono tabular-nums whitespace-nowrap">
                          {fmtMoney(b.remaining_balance)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
                  Income booked next week
                </div>
                {nextWeek.incomes.length === 0 ? (
                  <div className="text-sm text-neutral-500">None.</div>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {nextWeek.incomes.map((i) => (
                      <li
                        key={i.id}
                        className="py-2 flex items-center justify-between gap-3 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-display font-bold truncate">
                            {i.client_name}
                          </div>
                          <div className="text-xs text-neutral-500 truncate">
                            {i.category} · {fmtDate(i.expected_date)}
                          </div>
                        </div>
                        <div
                          className={`font-display font-extrabold mono tabular-nums whitespace-nowrap ${i.received ? "text-green-700" : "text-amber-700"}`}
                        >
                          {fmtMoney(i.amount)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Recent trend (optional, prints small) */}
        {trend && trend.weeks && trend.weeks.length > 0 && (
          <section className="border-2 border-black p-5 mb-6 break-inside-avoid">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
              Last {trend.weeks.length} weeks at a glance
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={trend.weeks}
                  margin={{ top: 10, right: 8, left: -10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke="#E5E7EB"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{
                      fontSize: 10,
                      fontFamily: "IBM Plex Sans",
                      letterSpacing: "0.08em",
                    }}
                    stroke="#9CA3AF"
                  />
                  <YAxis
                    tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }}
                    stroke="#9CA3AF"
                    tickFormatter={(v) =>
                      v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                    }
                  />
                  <Tooltip
                    formatter={(v) =>
                      Number(v).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }
                    contentStyle={{
                      borderRadius: 0,
                      border: "2px solid #111",
                      fontSize: 11,
                    }}
                  />
                  <RBar dataKey="outgoing" fill="#FF3B30" name="Out" />
                  <RBar dataKey="received" fill="#008A00" name="Received" />
                  <RBar dataKey="expected" fill="#002FA7" name="Expected" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="border-t-2 border-black pt-4 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold flex items-center justify-between">
          <span>Billy. — Weekly Cashflow Report</span>
          <span>{printDate}</span>
        </footer>
      </div>

      {/* Print CSS — strip nav, tighten margins */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:py-4 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-6 { padding-left: 0 !important; padding-right: 0 !important; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default WeeklyPrint;
